import React, { useState, useRef, useEffect } from 'react';
import useSupercluster from "use-supercluster";
import GoogleMapReact from "google-map-react";
import { Marker } from 'react-google-maps';
import InfoWindow from '../InfoWindow';
import './Map.css';

// Estilos melhorados para o tema escuro do mapa com maior contraste e nitidez
const darkMapStyles = [
  { elementType: "geometry", stylers: [{ color: "#1f2a37" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#000000" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#ffffff" }] },
  { elementType: "labels.icon", stylers: [{ visibility: "on" }, { saturation: 50 }] },
  {
    featureType: "administrative",
    elementType: "geometry.stroke",
    stylers: [{ color: "#4a5568" }]
  },
  {
    featureType: "administrative.locality",
    elementType: "labels.text.fill",
    stylers: [{ color: "#d59563" }]
  },
  {
    featureType: "poi",
    elementType: "labels.text.fill",
    stylers: [{ color: "#e9e9e9" }]
  },
  {
    featureType: "poi.park",
    elementType: "geometry",
    stylers: [{ color: "#263c3f" }]
  },
  {
    featureType: "poi.park",
    elementType: "labels.text.fill",
    stylers: [{ color: "#8fad15" }]
  },
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [{ color: "#38414e" }]
  },
  {
    featureType: "road",
    elementType: "geometry.stroke",
    stylers: [{ color: "#212a37" }]
  },
  {
    featureType: "road",
    elementType: "labels.text.fill",
    stylers: [{ color: "#ffffff" }]
  },
  {
    featureType: "road.highway",
    elementType: "geometry",
    stylers: [{ color: "#746855" }]
  },
  {
    featureType: "road.highway",
    elementType: "geometry.stroke",
    stylers: [{ color: "#1f2835" }]
  },
  {
    featureType: "road.highway",
    elementType: "labels.text.fill",
    stylers: [{ color: "#f3d19c" }]
  },
  {
    featureType: "transit",
    elementType: "geometry",
    stylers: [{ color: "#2f3948" }]
  },
  {
    featureType: "transit.station",
    elementType: "labels.text.fill",
    stylers: [{ color: "#d59563" }]
  },
  {
    featureType: "water",
    elementType: "geometry",
    stylers: [{ color: "#17263c" }]
  },
  {
    featureType: "water",
    elementType: "labels.text.fill",
    stylers: [{ color: "#70b5ff" }]
  }
];

// Estilos melhorados para o tema claro com melhor nitidez
const lightMapStyles = [
  {
    featureType: "water",
    elementType: "geometry",
    stylers: [{ color: "#c0d6eb" }]
  },
  {
    featureType: "poi.park",
    elementType: "geometry.fill",
    stylers: [{ color: "#c3e6a3" }]
  },
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [{ color: "#ffffff" }]
  },
  {
    featureType: "road.highway",
    elementType: "geometry",
    stylers: [{ color: "#f5d480" }]
  }
];

const Map = (props) => {
  const mapRef = useRef();

  const { points, mapPoints, darkMode } = props;
  const [bounds, setBounds] = useState(null);
  const [zoom, setZoom] = useState(5);

  const [center, setCenter] = useState({
    lat: -14.4284,
    lng: -55.2733
  });
  
  const [mapStyles, setMapStyles] = useState(darkMode ? darkMapStyles : lightMapStyles);

  // Atualiza os estilos do mapa quando o tema muda
  useEffect(() => {
    setMapStyles(darkMode ? darkMapStyles : lightMapStyles);
  }, [darkMode]);

  const handleCloseInfoWindown = () => {
    setSelectedEquipment(null);
    setCenter({
      lat: -14.4284,
      lng: -55.2733
    });
    setZoom(5);
  }

  const { clusters, supercluster } = useSupercluster({
    points,
    bounds,
    zoom,
    options: { radius: 30, maxZoom: 15 }
  });

  const [selectedEquipment, setSelectedEquipment] = useState(null);

  const mapOptions = (isDark) => ({
    styles: isDark ? darkMapStyles : lightMapStyles,
    fullscreenControl: false,
    zoomControl: true,
    mapTypeControl: true,
    streetViewControl: false,
    rotateControl: false,
    clickableIcons: false,
    scrollwheel: true,
    disableDoubleClickZoom: false,
    mapTypeControlOptions: {
      style: 2, // DROPDOWN_MENU
      position: 3 // RIGHT_TOP
    }
  });

  return (
    <div style={{ height: '100%', width: '100%' }}>
      <GoogleMapReact
        bootstrapURLKeys={{ key: process.env.REACT_APP_GOOGLE_API_KEY }}
        defaultCenter={{
          lat: -14.4284,
          lng: -55.2733
        }}
        center={center}
        zoom={zoom}
        defaultZoom={4}
        options={mapOptions(darkMode)}
        yesIWantToUseGoogleMapApiInternals
        onGoogleApiLoaded={({ map }) => {
          mapRef.current = map;
        }}
        onChange={({ zoom, bounds }) => {
          setZoom(zoom);
          setBounds([
            bounds.nw.lng,
            bounds.se.lat,
            bounds.se.lng,
            bounds.nw.lat
          ]);
        }}
      >
        {clusters.map(marker => {
          const [longitude, latitude] = marker.geometry.coordinates;
          const {
            cluster: isCluster,
            point_count: pointCount
          } = marker.properties;

          if (mapPoints === true) {
            return (
              <Marker
                key={marker.id || `marker-${latitude}-${longitude}`}
                onClick={() => {
                  setSelectedEquipment(marker);
                }}
                lat={latitude}
                lng={longitude}
              >
                <img
                  alt=""
                  src={marker ? '/images/redMarker.png' : ''}
                  className="marker-image"
                  onClick={() => {
                    setSelectedEquipment(marker.properties.data)
                  }} 
                />
              </Marker>
            )
          }

          if (isCluster) {
            return (
              <Marker
                key={marker.id || `marker-${latitude}-${longitude}`}
                onClick={() => {
                  setSelectedEquipment(marker);
                }}
                lat={latitude}
                lng={longitude}
              >
                <div
                  className="cluster map-cluster"
                  style={{
                    width: `${15 + (pointCount / points.length) * 30}px`,
                    height: `${15 + (pointCount / points.length) * 30}px`,
                    zIndex: 9999
                  }}
                  onClick={() => {
                    const expansionZoom = Math.min(
                      supercluster.getClusterExpansionZoom(marker.id),
                      20
                    );
                    mapRef.current.setZoom(expansionZoom);
                    mapRef.current.panTo({ lat: latitude, lng: longitude });
                  }}
                >
                  {pointCount}
                </div>
              </Marker>
            )
          }

          return (
            <Marker
              key={marker.id || `marker-${latitude}-${longitude}`}
              lat={latitude}
              lng={longitude}
            >
              <div className="marker-container map-marker-container" style={{ position: 'relative' }}>
                {marker?.properties?.data?.isConnected ?
                  <div
                    className="badge-online map-badge-online"
                    style={{
                      position: 'absolute',
                      top: '-15px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      color: 'var(--map-online-badge-text)',
                      fontSize: '10px',
                      padding: '2px 6px',
                      borderRadius: '10px',
                      zIndex: 998,
                      boxShadow: 'var(--map-marker-shadow)',
                      border: "solid 1px white"
                    }}
                  >
                    {"Online"}
                  </div>
                  : null
                }

                <span className="marker-label">{marker?.properties?.data?.serial}</span>
                <img
                  alt="img"
                  src={
                    marker.properties.data.serial.includes("CNMT")
                      ? '/images/cn.png'
                      : marker.properties.data.serial.startsWith("CP")
                        ? '/images/cp.png'
                        : marker.properties.data.serial.startsWith("SI") || marker.properties.data.serial.startsWith("ES")
                          ? '/images/esi.png'
                          : marker.properties.data.serial.startsWith("BI") || marker.properties.data.serial.startsWith("FB")
                            ? '/images/fbi.png'
                            : marker.properties.data.serial.startsWith("EE")
                              ? '/images/ee.png'
                              : '/images/999dry.png'
                  }
                  className={`marker ${marker.located ? 'equip_marker_img_located' : 'equip_marker_img'}`}
                  onClick={() => {
                    setSelectedEquipment(marker.properties.data);
                  }}
                />
              </div>
            </Marker>
          )
        })}

        {selectedEquipment &&
          <InfoWindow
            close={handleCloseInfoWindown}
            data={selectedEquipment}
            lat={selectedEquipment.lat}
            lng={selectedEquipment.lng}
            setCenter={setCenter}
            darkMode={darkMode}
          />
        }
      </GoogleMapReact>
    </div>
  )
}

export default Map;
